import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { User } from '../types';

interface MenuItem {
  label: string;
  path: string;
  icon: string;
  adminOnly?: boolean;
  coachHidden?: boolean;
}

interface MenuGroup {
  title: string;
  items: MenuItem[];
  adminOnly?: boolean;
}

const menuGroups: MenuGroup[] = [
  {
    title: '教学管理',
    items: [
      { label: '日历', path: '/calendar', icon: 'fa-calendar-alt' },
      { label: '课程', path: '/courses', icon: 'fa-list', coachHidden: true },
      { label: '教练', path: '/coach-management', icon: 'fa-user-tie' },
      { label: '学员', path: '/student-management', icon: 'fa-users', coachHidden: true },
      { label: '场地', path: '/venue-management', icon: 'fa-map-marker-alt', adminOnly: true },
    ],
  },
  {
    title: '课时运营',
    items: [
      { label: '课时包', path: '/packages', icon: 'fa-box', coachHidden: true },
      { label: '定价配置', path: '/package-pricing', icon: 'fa-tags', adminOnly: true },
      { label: '进度', path: '/progress', icon: 'fa-chart-bar' },
      { label: '看板', path: '/kanban', icon: 'fa-columns' },
    ],
  },
  {
    title: '教学资源',
    items: [
      { label: '教案库', path: '/lesson-plans', icon: 'fa-book-open' },
      { label: '知识图谱', path: '/lesson-plans/graph', icon: 'fa-project-diagram' },
      { label: '战术锦囊', path: '/tactical-capsules', icon: 'fa-briefcase-medical' },
      { label: '视频分析', path: '/video-analysis', icon: 'fa-video' },
    ],
  },
  {
    title: '数据中心',
    adminOnly: true,
    items: [
      { label: '报表', path: '/reports', icon: 'fa-file-alt' },
      { label: '导入导出', path: '/data-export', icon: 'fa-exchange-alt' },
    ],
  },
  {
    title: '系统',
    items: [
      { label: '配置', path: '/settings', icon: 'fa-cog' },
    ],
  },
];

const mobileTabs = [
  { label: '课表', path: '/calendar', icon: 'fa-calendar-alt' },
  { label: '教案', path: '/lesson-plans', icon: 'fa-book-open' },
  { label: '进度', path: '/progress', icon: 'fa-chart-bar' },
  { label: '视频', path: '/video-analysis', icon: 'fa-video' },
];

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const [user, setUser] = useState<User | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try { setUser(JSON.parse(userStr)); } catch {}
    }
  }, []);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    window.location.href = '/#/login';
  };

  const isActive = (path: string) => {
    if (location.pathname === path) return true;
    if (path === '/calendar' && location.pathname === '/') return true;
    if (path === '/lesson-plans' && location.pathname.startsWith('/lesson-plans/') && !location.pathname.startsWith('/lesson-plans/graph')) return true;
    if (path === '/tactical-capsules' && location.pathname.startsWith('/tactical-capsules/')) return true;
    return false;
  };

  const isAdmin = user?.role === 'admin';
  const isVenueManager = user?.role === 'venue_manager';
  const isCoach = user?.role === 'coach';
  const isJiaowu = user?.role === 'jiaowu';

  // venue_manager 只能看到日历；coach 隐藏 coachHidden 菜单项
  const visibleMenuGroups = isVenueManager
    ? menuGroups
        .map(g => ({ ...g, items: g.items.filter(item => item.path === '/calendar') }))
        .filter(g => g.items.length > 0 && !g.adminOnly)
    : menuGroups
        .map(g => ({ ...g, items: g.items.filter(item => !(item.coachHidden && isCoach)) }));

  const visibleMobileTabs = isVenueManager
    ? mobileTabs.filter(tab => tab.path === '/calendar')
    : mobileTabs;

  return (
    <div className="min-h-screen bg-canvas">
      {/* PC 顶部 nav（≥ md 可见） */}
      <header className="top-nav sticky top-0 z-30 backdrop-blur-md bg-canvas/95 hidden md:block">
        <div className="max-w-content mx-auto px-6 lg:px-8">
          {/* 第一行：品牌 + 用户 */}
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2 group">
              <span className="w-8 h-8 rounded-md bg-primary-500 grid place-items-center text-white">
                <span className="text-base">🏸</span>
              </span>
              <span className="text-title-md font-semibold text-ink tracking-tight">橙羽体育</span>
            </Link>
            <div className="flex items-center gap-3">
              {user && (
                <div className="flex items-center gap-2">
                  <span className="w-8 h-8 rounded-full bg-surface-card text-ink text-[13px] font-semibold grid place-items-center">
                    {user.username.slice(0, 1).toUpperCase()}
                  </span>
                  <span className="text-[13px] text-muted">
                    {user.username}
                    {isAdmin && <span className="ml-1.5 badge badge-orange">管理员</span>}
                    {isJiaowu && <span className="ml-1.5 badge badge-green">教务管理</span>}
                    {isVenueManager && <span className="ml-1.5 badge badge-blue">场地管理</span>}
                  </span>
                </div>
              )}
              <button
                onClick={handleLogout}
                className="text-[13px] text-muted hover:text-ink transition-colors"
              >
                登出
              </button>
            </div>
          </div>
          {/* 第二行：分组导航 */}
          <nav className="flex items-stretch gap-5 pt-2 pb-3 overflow-x-auto">
            {visibleMenuGroups.map((group, gi) => {
              if (group.adminOnly && !isAdmin) return null;
              const isFirst = gi === 0;
              return (
                <div key={group.title} className={`flex flex-col shrink-0 ${isFirst ? '' : 'pl-5 border-l border-hairline-soft'}`}>
                  <span className="text-[11px] font-medium uppercase tracking-wider text-muted-soft mb-1.5 px-1 select-none">
                    {group.title}
                  </span>
                  <div className="flex items-center gap-1">
                    {group.items.filter(item => !item.adminOnly || isAdmin).map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`top-nav-link whitespace-nowrap ${isActive(item.path) ? 'top-nav-link-active' : ''}`}
                      >
                        <i className={`fas ${item.icon} mr-1.5 text-[12px] opacity-70`}></i>
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </div>
              );
            })}
          </nav>
        </div>
      </header>

      {/* 移动端顶部栏（< md 可见） */}
      <header className="sticky top-0 z-30 bg-canvas/95 backdrop-blur-md border-b border-hairline-soft md:hidden">
        <div className="flex items-center justify-between h-14 px-4">
          <Link to="/" className="flex items-center gap-2">
            <span className="w-7 h-7 rounded-md bg-primary-500 grid place-items-center text-white">
              <span className="text-sm">🏸</span>
            </span>
            <span className="text-title-sm font-semibold text-ink tracking-tight">橙羽体育</span>
          </Link>
          <button
            onClick={() => setDrawerOpen(true)}
            className="w-9 h-9 grid place-items-center rounded-md text-muted hover:text-ink hover:bg-surface-soft transition-colors"
            aria-label="菜单"
          >
            <i className="fas fa-bars text-lg"></i>
          </button>
        </div>
      </header>

      {/* 移动端抽屉 */}
      {drawerOpen && (
        <div className="fixed inset-0 z-40">
          {/* 蒙层 */}
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setDrawerOpen(false)}
          />
          {/* 面板 */}
          <div className="absolute top-0 right-0 h-full w-80 max-w-[85vw] bg-canvas shadow-card animate-slide-in flex flex-col z-50">
            {/* 抽屉头 */}
            <div className="flex items-center justify-between px-5 h-14 border-b border-hairline-soft shrink-0">
              {user && (
                <div className="flex items-center gap-2">
                  <span className="w-8 h-8 rounded-full bg-surface-card text-ink text-[13px] font-semibold grid place-items-center">
                    {user.username.slice(0, 1).toUpperCase()}
                  </span>
                  <span className="text-body-sm text-ink font-medium">
                    {user.username}
                    {isAdmin && <span className="ml-1.5 badge badge-orange">管理员</span>}
                    {isJiaowu && <span className="ml-1.5 badge badge-green">教务管理</span>}
                    {isVenueManager && <span className="ml-1.5 badge badge-blue">场地管理</span>}
                  </span>
                </div>
              )}
              <button
                onClick={() => setDrawerOpen(false)}
                className="w-8 h-8 grid place-items-center rounded-md text-muted hover:text-ink transition-colors"
                aria-label="关闭菜单"
              >
                <i className="fas fa-times text-base"></i>
              </button>
            </div>

            {/* 菜单分组 */}
            <nav className="flex-1 overflow-y-auto py-3 px-3">
              {visibleMenuGroups.map((group) => {
                if (group.adminOnly && !isAdmin) return null;
                return (
                  <div key={group.title} className="mb-4">
                    <span className="text-[11px] font-medium uppercase tracking-wider text-muted-soft mb-1 px-3 block select-none">
                      {group.title}
                    </span>
                    <div className="flex flex-col gap-0.5">
                      {group.items.filter(item => !item.adminOnly || isAdmin).map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={() => setDrawerOpen(false)}
                          className={`flex items-center gap-2.5 px-3 py-2 rounded-md text-body-sm transition-colors ${
                            isActive(item.path)
                              ? 'bg-surface-card text-ink font-medium'
                              : 'text-muted hover:text-ink hover:bg-surface-soft'
                          }`}
                        >
                          <i className={`fas ${item.icon} w-4 text-center text-[13px] opacity-70`}></i>
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                );
              })}
            </nav>

            {/* 底部登出 */}
            <div className="border-t border-hairline-soft px-5 py-3 shrink-0">
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 text-body-sm text-muted hover:text-ink transition-colors w-full py-2"
              >
                <i className="fas fa-sign-out-alt text-[13px]"></i>
                登出
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="max-w-content mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-10 pb-20 md:pb-10">
        {children}
      </main>

      {/* PC 页脚 */}
      <footer className="mt-16 border-t border-hairline-soft hidden md:block">
        <div className="max-w-content mx-auto px-6 lg:px-8 py-8 flex items-center justify-between flex-wrap gap-3">
          <span className="text-[13px] text-muted-soft">橙羽体育 © {new Date().getFullYear()} · 羽毛球培训管理系统</span>
          <span className="text-[13px] text-muted-soft">v1.0</span>
        </div>
      </footer>

      {/* 移动端底部 tab 栏 */}
      <nav className="fixed bottom-0 inset-x-0 z-30 bg-canvas/95 backdrop-blur-md border-t border-hairline-soft md:hidden">
        <div className={`grid ${visibleMobileTabs.length === 1 ? 'grid-cols-1' : 'grid-cols-4'} h-14`}>
          {visibleMobileTabs.map((tab) => {
            const active = isActive(tab.path);
            return (
              <Link
                key={tab.path}
                to={tab.path}
                className={`flex flex-col items-center justify-center gap-0.5 transition-colors ${
                  active ? 'text-primary-500' : 'text-muted'
                }`}
              >
                <i className={`fas ${tab.icon} text-[18px]`}></i>
                <span className="text-[11px] font-medium">{tab.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default Layout;
